import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useRequest } from '@Contexts/Request';

import type { ProductData } from '@Types/Product';
import type { IRelatedProducts } from '@Types/RelatedProducts';

const useChangeQuantity = (mainId: number) => {
  const queryClient = useQueryClient();
  const { region, country } = useRequest();
  const key = ['related-products', mainId, { country, region }];

  return useMutation({
    mutationKey: key,
    mutationFn: async (data: { product: ProductData; quantity?: number }) => {
      const { product, quantity } = data;

      if (typeof quantity === 'number') {
        return { ...product, quantity };
      }

      return product;
    },
    onSuccess: (newProduct) => {
      queryClient.setQueryData<IRelatedProducts>(key, (data: IRelatedProducts) => {
        const { changedProducts: products } = data;

        const oldProduct = products.find((product) => product.id === newProduct.id);

        const wasFirstAdded =
          oldProduct.quantity < newProduct.quantity && newProduct.quantity === 1;

        if (wasFirstAdded) {
          return {
            ...data,
            changedProducts: [
              newProduct,
              ...products.filter((product) => product.id !== newProduct.id),
            ],
          };
        }

        const updatedProducts = products.map((product) =>
          product.id === newProduct.id ? newProduct : product,
        );
        const addedProducts: ProductData[] = [];
        const notaddedProducts: ProductData[] = [];

        updatedProducts.forEach((product) => {
          if (product.quantity) {
            addedProducts.push(product);
          } else {
            notaddedProducts.push(product);
          }
        });

        return { ...data, changedProducts: addedProducts.concat(...notaddedProducts) };
      });
    },
  });
};

export default useChangeQuantity;
